import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { PlayerWithScorecard } from '../../types/game'
import { useSound } from '../../hooks/useSound'
import WebApp from '@twa-dev/sdk'
import Confetti from './Confetti'
import Avatar from '../common/Avatar'

import { Tournament } from '../../types/tournament'

interface Props {
  players:    PlayerWithScorecard[]
  isAdmin:    boolean
  soundEnabled?: boolean
  tournament?: Tournament | null
  onCreateNewGame:  () => void
  onRematch: () => void;
  onBack: () => void;
  onNotifyResults?: () => Promise<void>;
  onShowStandings?: () => void;
  onNextTournamentGame?: () => void;
  onFinishTournament?: () => void;
  onClose?: () => void;
}

const PLACE_KEYS = ['winner.place1', 'winner.place2', 'winner.place3'] as const

export default function WinnerScreen({
  players,
  isAdmin,
  soundEnabled = true,
  tournament,
  onCreateNewGame,
  onRematch,
  onBack,
  onNotifyResults,
  onShowStandings,
  onNextTournamentGame,
  onFinishTournament,
  onClose,
}: Props) {
  const { t } = useTranslation()
  const { play } = useSound(soundEnabled)
  const [stage, setStage] = useState(0)
  const [notified, setNotified] = useState(false)

  const sorted = useMemo(
    () => [...players].sort((a, b) => b.totals.grand_total - a.totals.grand_total),
    [players],
  )

  // Handle ties for 1st place — all players with the top score are "winners"
  const topScore = sorted[0]?.totals.grand_total
  const winners = sorted.filter((p) => p.totals.grand_total === topScore)
  const rest = sorted.filter((p) => p.totals.grand_total !== topScore).slice(0, 3)

  useEffect(() => {
    const timers = [
      setTimeout(() => setStage(1), 50),
      setTimeout(() => setStage(2), 300),
      setTimeout(() => setStage(3), 650),
      setTimeout(() => setStage(4), 1050),
      setTimeout(() => setStage(5), 1400),
    ]
    return () => timers.forEach(clearTimeout)
  }, [])

  // Play win sound when trophy stage reached
  useEffect(() => {
    if (stage === 2) {
      play("win")
    }
  }, [stage, play])
  const show = (minStage: number, extra = '') =>
    `transition-all duration-500 ${extra} ${stage >= minStage ? 'opacity-100' : 'opacity-0'}`

  return (
    <div
      className={`fixed inset-0 z-50
        transition-all duration-500 ${stage >= 1 ? 'bg-gray-950/97 backdrop-blur-sm' : 'bg-transparent pointer-events-none'}`}
    >
      {/* ── Close button ───────────────────────────────────────── */}
      {onClose && (
        <button
          onClick={onClose}
          className={show(5, "absolute top-6 right-6 z-20 p-2 glass hover:bg-white/10 rounded-full text-white/50 hover:text-white transition-all")}
        >
          ✕
        </button>
      )}

      {/* ── Confetti ─────────────────────────────────────────── */}
      {stage >= 2 && <Confetti />}

      {/* Scroll container: content can exceed the viewport on small screens /
          large font scale; min-h-full keeps it centered when it fits. */}
      <div className="absolute inset-0 overflow-y-auto overscroll-contain">
      <div className="min-h-full flex flex-col items-center justify-center px-4 py-10">

      {/* ── Trophy ───────────────────────────────────────────── */}
      <div className={show(2, stage >= 2 ? 'scale-100' : 'scale-50')}>
        <div
          className="text-7xl text-center mb-2 select-none"
          style={{ animation: stage >= 2 ? 'trophyBounce 0.6s cubic-bezier(.36,.07,.19,.97) both' : 'none' }}
        >
          🏆
        </div>
      </div>

      {/* ── КАТАЛА ───────────────────────────────────────────── */}
      <div className={show(3, stage >= 3 ? 'translate-y-0' : 'translate-y-6')}>
        <h1
          className="text-6xl font-black text-center tracking-widest leading-none select-none"
          style={{
            color: '#FFD700',
            textShadow: '0 0 24px rgba(255,215,0,0.8), 0 0 48px rgba(255,215,0,0.4)',
            animation: stage >= 3 ? 'kataGlow 2s ease-in-out infinite alternate' : 'none',
          }}
        >
          {t('winner.title')}
        </h1>
        <p className="text-gray-400 text-center text-sm mt-1 tracking-wide">{t('winner.wins')}</p>
      </div>

      {/* ── Winner card(s) ──────────────────────────────────────── */}
      <div className={show(3, `mt-5 text-center flex flex-col items-center ${stage >= 3 ? 'scale-100' : 'scale-90'}`)}>
        <div className={`mb-3 flex justify-center ${winners.length > 1 ? 'gap-3' : ''}`}>
          {winners.map((w) => (
            <div key={w.player.id} className="flex flex-col items-center">
              <Avatar src={w.player.avatar || null} size={winners.length > 1 ? "lg" : "xl"} />
              <p className={`font-bold text-white mt-1 ${winners.length > 1 ? 'text-base' : 'text-2xl'}`}>
                {w.player.display_name}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Score badge ───────────────────────────────────────── */}
      <div className={show(4, 'mt-3')}>
        <div className="bg-yellow-500/15 border border-yellow-500/40 rounded-2xl px-8 py-3 text-center">
          <span
            className="text-5xl font-black text-yellow-400 tabular-nums"
            style={{ animation: stage >= 4 ? 'scoreReveal 0.4s ease-out both' : 'none' }}
          >
            {topScore}
          </span>
          <p className="text-xs text-gray-500 mt-0.5 uppercase tracking-wider">{t('winner.pts')}</p>
        </div>
      </div>

      {/* ── Share button ──────────────────────────────────────── */}
      <div className={show(5, 'mt-3 w-full max-w-sm')}>
        <button
          onClick={() => {
            const lines = sorted.map((p, i) => {
              const isWinner = p.totals.grand_total === topScore
              const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i+1}.`
              const prefix = isWinner && i > 0 ? '🥇' : medal
              return `${prefix} ${p.player.display_name}: ${p.totals.grand_total} ${t("winner.pts")}`
            }).join('\n');
            const msg = t("winner.shareResultsMessage", { title: t("app.title"), results: lines });
            try {
              WebApp.openTelegramLink(
                `https://t.me/share/url?url=${encodeURIComponent('https://t.me/')}&text=${encodeURIComponent(msg)}`
              );
            } catch {
              navigator.clipboard?.writeText(msg);
            }
          }}
          className="w-full py-3 bg-blue-900/40 border border-blue-700/50 hover:bg-blue-900/60 rounded-2xl text-blue-300 font-medium text-sm transition-colors flex items-center justify-center gap-2"
        >
          📤 {t('winner.shareResults', 'Share results')}
        </button>

        {onNotifyResults && (
          <button
            onClick={async () => {
              await onNotifyResults();
              setNotified(true);
              setTimeout(() => setNotified(false), 3000);
            }}
            disabled={notified}
            className={`w-full mt-2 py-3 border rounded-2xl font-medium text-sm transition-all flex items-center justify-center gap-2 ${
              notified 
                ? "bg-green-900/40 border-green-500/50 text-green-300" 
                : "bg-purple-900/40 border-purple-700/50 hover:bg-purple-900/60 text-purple-300"
            }`}
          >
            {notified ? '✅' : '🤖'} {t('winner.notifyPlayers', 'Notify Players')}
          </button>
        )}
      </div>

      {/* ── Runners-up ────────────────────────────────────────── */}
      {rest.length > 0 && (
        <div className={show(5, `mt-6 flex gap-2 justify-center flex-wrap ${stage >= 5 ? 'translate-y-0' : 'translate-y-4'}`)}>
          {rest.map((p, i) => (
            <div key={p.player.id} className="text-center glass rounded-2xl px-4 py-3 min-w-[80px] flex flex-col items-center">
              <p className="text-xs text-gray-500 mb-2">{t(PLACE_KEYS[i + 1] ?? 'winner.place3')}</p>
              <div className="mb-2 flex justify-center">
                <Avatar src={p.player.avatar || null} size="lg" />
              </div>
              <p className="text-xs font-semibold text-gray-300 truncate max-w-[64px] mx-auto">
                {p.player.display_name}
              </p>
              <p className="text-sm font-bold text-gray-400">{p.totals.grand_total}</p>
            </div>
          ))}
        </div>
      )}

      {/* ── Buttons ───────────────────────────────────────────── */}
      <div className={show(5, `mt-8 flex flex-col gap-3 w-full max-w-sm ${stage >= 5 ? 'translate-y-0' : 'translate-y-4'}`)}>
        {onShowStandings && (
          <button
            onClick={onShowStandings}
            className="w-full py-4 bg-yellow-500 text-black font-black rounded-2xl shadow-xl transform transition-all hover:scale-[1.02] active:scale-95 uppercase tracking-tighter"
          >
            📊 {t('tournament.standings')}
          </button>
        )}
        
        <div className="flex gap-3">
          <button
            onClick={onBack}
            className="flex-1 py-3 glass hover:bg-white/10 rounded-2xl font-medium transition-colors"
          >
            {t('winner.backToLobby')}
          </button>
          
          {tournament ? (
            isAdmin && (
              tournament.current_game_index >= tournament.total_games
                ? onFinishTournament && (
                    <button
                      onClick={onFinishTournament}
                      className="flex-1 py-3 bg-yellow-500 hover:bg-yellow-400 text-black rounded-2xl font-semibold transition-colors"
                    >
                      🏆 {t('tournament.finishTournament')}
                    </button>
                  )
                : onNextTournamentGame && (
                    <button
                      onClick={onNextTournamentGame}
                      className="flex-1 py-3 bg-blue-600 hover:bg-blue-500 rounded-2xl font-semibold transition-colors"
                    >
                      {t('tournament.nextGame')}
                    </button>
                  )
            )
          ) : (
            isAdmin ? (
              <button
                onClick={onRematch}
                className="flex-1 py-3 bg-blue-600 hover:bg-blue-500 rounded-2xl font-semibold transition-colors"
              >
                {t('winner.rematch', 'Rematch')}
              </button>
            ) : (
              <button
                onClick={onCreateNewGame}
                className="flex-1 py-3 glass hover:bg-white/10 rounded-2xl font-semibold transition-colors"
              >
                {t('winner.newGame')}
              </button>
            )
          )}
        </div>
      </div>
      </div>
      </div>
    </div>
  )
}
